import {
  FieldDataWithValidate,
  FieldData,
} from 'app/components/Builder/Strategy/fields/types';
import { SaveUserBotParams } from 'types/ApiServicesTypes';
import { InfoType } from 'types/ComponentTypes';
import { ComboBox } from 'types/Generic';
import { Instrument } from 'types/Instrument';
import { formatDateString } from 'utils/GenericFunctions';
import {
  StrategyBuilderSymbolsFormData,
  StrategyBuilderEntryExitFormData,
  StrategyBuilderPortfolioFormData,
  LongShortEntryExit,
  SbStrategy,
  StrategyBuilderFormData,
} from './types';
import FormattedMessage from 'app/design/uiComponents/FormattedMessage';

export const ChartTypesData: ComboBox[] = [
  { label: 'Candlestick', value: 'Candlestick' },
];

export const IntervalsData: ComboBox[] = [
  { label: '1 Min', value: '00:01:00' },
  { label: '2 Min', value: '00:02:00' },
  { label: '3 Min', value: '00:03:00' },
  { label: '5 Min', value: '00:05:00' },
  { label: '10 Min', value: '00:10:00' },
  { label: '15 Min', value: '00:15:00' },
  { label: '20 Min', value: '00:20:00' },
  { label: '25 Min', value: '00:25:00' },
  { label: '30 Min', value: '00:30:00' },
  { label: '45 Min', value: '00:45:00' },
  { label: '1 Hour', value: '01:00:00' },
  { label: '75 Min', value: '01:15:00' },
  { label: '2 Hour', value: '02:00:00' },
  { label: '125 Min', value: '02:05:00' },
  { label: '3 Hour', value: '03:00:00' },
  { label: '4 Hour', value: '04:00:00' },
  { label: '1 Day', value: '24:00:00' },
];

export const PositionDirectionData: ComboBox[] = [
  { label: 'Long', value: 'long' },
  { label: 'Short', value: 'short' },
  { label: 'Both', value: 'both' },
];

export const RiskTypeData: ComboBox[] = [
  { label: 'Points', value: 'points' },
  { label: 'Percentage', value: 'percentage' },
];
export const TrailingOptions: ComboBox[] = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
];

export const PositioningSizingData: ComboBox[] = [
  { label: 'Quantity Based', value: 'quantity_based' },
  { label: 'Capital Based (Absolute)', value: 'capital_based_absolute' },
  { label: 'Capital Based (%)', value: 'capital_based_percentage' },
  { label: 'Risk Based', value: 'risk_based' },
];

export const OrderTypeData: ComboBox[] = [
  { label: 'Limit', value: 'limit' },
  { label: 'Market', value: 'market' },
];

export const HoldingTypeData: ComboBox[] = [
  { label: 'CNC / NRML', value: 'cnc' },
  { label: 'MIS / Intraday', value: 'mis' },
];

export const isValidConditions = (
  conditions: FieldDataWithValidate,
  type: 'entry' | 'exit',
): boolean => {
  if (type === 'entry')
    return (conditions &&
      conditions.conditions.length &&
      conditions.isConditionValid) as boolean;
  else return conditions && conditions.isConditionValid;
};

export const transformSaveUserBotRequest = (
  symbolsFormData: StrategyBuilderSymbolsFormData,
  entryExitFormData: StrategyBuilderEntryExitFormData,
  portfolioFormData: StrategyBuilderPortfolioFormData,
  strategyId?: number | null, // <-- new optional param
): SaveUserBotParams => {
  // eslint-disable-next-line no-console
  console.log('enteryExitFormData', entryExitFormData);
  const blanckConditions: LongShortEntryExit = {
    condition_key: '',
    conditions: [],
  };

  // Helper to check for Chartink
  const hasChartink = (arr: FieldData[] = []) =>
    Array.isArray(arr) && arr.some((c: FieldData) => c.key === 'chartink');
  // Remove Chartink from conditions
  const filterChartink = (arr: FieldData[] = []) =>
    arr.filter((c: FieldData) => c.key !== 'chartink');

  // Detect Chartink in each section
  const longEntryHasChartink = hasChartink(
    entryExitFormData.longEntryConditions?.conditions,
  );
  const longExitHasChartink = hasChartink(
    entryExitFormData.longExitConditions?.conditions,
  );
  const shortEntryHasChartink = hasChartink(
    entryExitFormData.shortEntryConditions?.conditions,
  );
  const shortExitHasChartink = hasChartink(
    entryExitFormData.shortExitConditions?.conditions,
  );

  // Build external_signal if any Chartink present
  let external_signal: Record<string, unknown> | undefined = undefined;
  if (
    longEntryHasChartink ||
    shortEntryHasChartink ||
    longExitHasChartink ||
    shortExitHasChartink
  ) {
    external_signal = {
      enable: true,
      source: 'chartink',
      entry_msg_data:
        longEntryHasChartink || shortEntryHasChartink
          ? { strategy_id: strategyId ?? null, signal_type: 301 }
          : null,
      exit_msg_data:
        longExitHasChartink || shortExitHasChartink
          ? { strategy_id: strategyId ?? null, signal_type: 302 }
          : null,
    };
  }

  // Remove Chartink from all conditions arrays if present
  const longEntryArr = entryExitFormData.longEntryConditions?.conditions || [];
  const cleanLongEntry: FieldData[] =
    longEntryArr.length === 1 && longEntryArr[0].key === 'chartink'
      ? []
      : filterChartink(longEntryArr);

  const longExitArr = entryExitFormData.longExitConditions?.conditions || [];
  const cleanLongExit: FieldData[] =
    longExitArr.length === 1 && longExitArr[0].key === 'chartink'
      ? []
      : filterChartink(longExitArr);

  const shortEntryArr = entryExitFormData.shortEntryConditions?.conditions || [];
  const cleanShortEntry: FieldData[] =
    shortEntryArr.length === 1 && shortEntryArr[0].key === 'chartink'
      ? []
      : filterChartink(shortEntryArr);

  const shortExitArr = entryExitFormData.shortExitConditions?.conditions || [];
  const cleanShortExit: FieldData[] =
    shortExitArr.length === 1 && shortExitArr[0].key === 'chartink'
      ? []
      : filterChartink(shortExitArr);

  const objReq: SaveUserBotParams = {
    name: symbolsFormData.botName,
    sb_strategy: {
      chart: symbolsFormData.chartType,
      universe: symbolsFormData.symbols.map(x => {
        return {
          name: x.name,
          segment: x.exchange_ids,
          symbol: x.trading_symbol,
        };
      }),
      resolution: symbolsFormData.interval,
      direction: entryExitFormData.positionDirection,
      long_entry:
        cleanLongEntry && cleanLongEntry.length
          ? ({
              condition_key: 'LongEntryConditions',
              conditions: cleanLongEntry,
            } as LongShortEntryExit)
          : blanckConditions,
      long_exit:
        cleanLongExit && cleanLongExit.length
          ? ({
              condition_key: 'LongExitConditions',
              conditions: cleanLongExit,
            } as LongShortEntryExit)
          : blanckConditions,
      short_entry:
        cleanShortEntry && cleanShortEntry.length
          ? ({
              condition_key: 'ShortEntryConditions',
              conditions: cleanShortEntry,
            } as LongShortEntryExit)
          : blanckConditions,
      short_exit:
        cleanShortExit && cleanShortExit.length
          ? ({
              condition_key: 'ShortExitConditions',
              conditions: cleanShortExit,
            } as LongShortEntryExit)
          : blanckConditions,
      risk_type: entryExitFormData.targetType,
      order_sizing: {
        type: portfolioFormData.positioningSizing,
        value: portfolioFormData.positionValue,
      },
      trailing_sl_type: entryExitFormData.targetType,
      risk_percentage: portfolioFormData.maxRiskAmount,
      order_type: portfolioFormData.orderType,
      order_valid_till: portfolioFormData.limitOrderValid
        ? portfolioFormData.limitOrderValid
        : '0',
      holding_type: portfolioFormData.holdingType,
      square_off_time:
        portfolioFormData.holdingType === 'cnc'
          ? '00:00'
          : portfolioFormData.squareOffTime,
      target_per: entryExitFormData.target,
      stop_loss_per: entryExitFormData.stoploss,
      trailing_sl_trigger_value: entryExitFormData.trailingTriggerValue,
      trailing_sl_value: entryExitFormData.trailingValue,
      trailing_sl_enabled: entryExitFormData.trailing_sl_enabled,
      trailling_sl: entryExitFormData.trailStoploss,
      start_date: formatDateString(
        new Date(new Date().setMonth(new Date().getMonth() - 30)),
        'DD/MM/YYYY',
      ),
      end_date: formatDateString(
        new Date(new Date().setDate(new Date().getDate() - 1)),
        'DD/MM/YYYY',
      ),
      initial_capital: '1000000',
      trading_start_time: '09:00',
      trading_stop_time: '23:59',
      ...(external_signal ? { external_signal } : {}),
    } as SbStrategy,
  };

  return objReq;
};

export const transformSaveUserBotResponse = (
  userBotDetails: SaveUserBotParams | undefined,
): StrategyBuilderFormData | null => {
  if (
    !userBotDetails ||
    !userBotDetails.sb_strategy ||
    !userBotDetails.sb_strategy.direction
  )
    return null;

  const botStrategy = userBotDetails.sb_strategy;

  // If Chartink is present in external_signal, inject Chartink condition into the relevant arrays for frontend rendering
  const chartinkField: FieldData = {
    key: 'chartink',
    type: 'external_signal',
    params: { label: 'chartink' } as { label: string },
  };
  const hasChartink =
    botStrategy.external_signal &&
    botStrategy.external_signal.enable === true &&
    botStrategy.external_signal.source === 'chartink';

  // Helper to check if Chartink is present for entry/exit
  const entryChartink = !!(hasChartink && botStrategy.external_signal?.entry_msg_data);
  const exitChartink = !!(hasChartink && botStrategy.external_signal?.exit_msg_data);

  // Helper to filter Chartink from conditions
  const filterChartink = (arr: FieldData[] = []) =>
    arr.filter((c: FieldData) => c.key !== 'chartink');

  // Helper to merge Chartink and normal conditions for frontend
  const mergeChartink = (chartink: boolean, arr: FieldData[] = []): FieldData[] => {
    if (chartink && (!arr || arr.length === 0)) return [chartinkField];
    if (chartink && arr && arr.length > 0) {
      const filtered = filterChartink(arr);
      return [chartinkField, ...filtered];
    }
    return arr || [];
  };

  // Direction logic
  const direction = botStrategy.direction;

  // Prepare all blocks with explicit types and const
  const longEntry: FieldData[] = Array.isArray(botStrategy.long_entry?.conditions)
    ? botStrategy.long_entry?.conditions
    : [];
  const longExit: FieldData[] = Array.isArray(botStrategy.long_exit?.conditions)
    ? botStrategy.long_exit?.conditions
    : [];
  const shortEntry: FieldData[] = Array.isArray(botStrategy.short_entry?.conditions)
    ? botStrategy.short_entry?.conditions
    : [];
  const shortExit: FieldData[] = Array.isArray(botStrategy.short_exit?.conditions)
    ? botStrategy.short_exit?.conditions
    : [];

  let longEntryConditions: FieldData[] = [];
  let longExitConditions: FieldData[] = [];
  let shortEntryConditions: FieldData[] = [];
  let shortExitConditions: FieldData[] = [];

  if (direction === 'both') {
    longEntryConditions = mergeChartink(entryChartink, longEntry);
    longExitConditions = mergeChartink(exitChartink, longExit);
    shortEntryConditions = mergeChartink(entryChartink, shortEntry);
    shortExitConditions = mergeChartink(exitChartink, shortExit);
  } else if (direction === 'long') {
    longEntryConditions = mergeChartink(entryChartink, longEntry);
    longExitConditions = mergeChartink(exitChartink, longExit);
    shortEntryConditions = shortEntry;
    shortExitConditions = shortExit;
  } else if (direction === 'short') {
    longEntryConditions = longEntry;
    longExitConditions = longExit;
    shortEntryConditions = mergeChartink(entryChartink, shortEntry);
    shortExitConditions = mergeChartink(exitChartink, shortExit);
  }

  const objReq: StrategyBuilderFormData = {
    botName: userBotDetails.name,
    symbols: {
      botName: userBotDetails.name,
      chartType: botStrategy.chart,
      interval: botStrategy.resolution,
      symbols: botStrategy.universe
        ? botStrategy.universe.map(x => {
            return {
              name: x.name,
              exchange_ids: x.segment,
              trading_symbol: x.symbol,
            } as Instrument;
          })
        : [],
    } as StrategyBuilderSymbolsFormData,
    entryExitData: {
      positionDirection: botStrategy.direction,
      longEntryConditions: {
        conditions: longEntryConditions,
        isConditionValid: true,
      },
      longExitConditions: {
        conditions: longExitConditions,
        isConditionValid: true,
      },
      shortEntryConditions: {
        conditions: shortEntryConditions,
        isConditionValid: true,
      },
      shortExitConditions: {
        conditions: shortExitConditions,
        isConditionValid: true,
      },
      targetType: botStrategy.risk_type,
      target: botStrategy.target_per,
      stoploss: botStrategy.stop_loss_per,
      trailingTriggerValue:
        botStrategy.trailing_sl_trigger_value != null
          ? Number(botStrategy.trailing_sl_trigger_value)
          : undefined,

      trailingValue:
        botStrategy.trailing_sl_value != null
          ? Number(botStrategy.trailing_sl_value)
          : undefined,
      isTrailStoploss: Boolean(botStrategy.trailing_sl_enabled),
      trailStoploss: botStrategy.trailling_sl,
      trailing_sl_enabled: Boolean(botStrategy.trailing_sl_enabled),
    },
    portfolioData: {
      positioningSizing: botStrategy.order_sizing.type,
      positionValue: botStrategy.order_sizing.value,
      orderType: botStrategy.order_type,
      holdingType: botStrategy.holding_type,
      squareOffTime: botStrategy.square_off_time,
      limitOrderValid: botStrategy.order_valid_till
        ? parseInt(botStrategy.order_valid_till)
        : 0,
      maxRiskAmount: botStrategy.risk_percentage,
    },
  };

  return objReq;
};

export const positionSizingInfoModalData: InfoType[] = [
  {
    title: (
      <FormattedMessage id="user_bot_startegy.position_sizing_info_modal.quantity_based.title" />
    ),
    desc: (
      <FormattedMessage id="user_bot_startegy.position_sizing_info_modal.quantity_based.desc" />
    ),
  },
  {
    title: (
      <FormattedMessage id="user_bot_startegy.position_sizing_info_modal.capital_based_absolute.title" />
    ),
    desc: (
      <FormattedMessage id="user_bot_startegy.position_sizing_info_modal.capital_based_absolute.desc" />
    ),
  },
  {
    title: (
      <FormattedMessage id="user_bot_startegy.position_sizing_info_modal.capital_based_percent.title" />
    ),
    desc: (
      <FormattedMessage id="user_bot_startegy.position_sizing_info_modal.capital_based_percent.desc" />
    ),
  },
  {
    title: (
      <FormattedMessage id="user_bot_startegy.position_sizing_info_modal.risk_based.title" />
    ),
    desc: (
      <FormattedMessage id="user_bot_startegy.position_sizing_info_modal.risk_based.desc" />
    ),
  },
];

export enum StrategyBuilderTypes {
  'equity',
  'option',
  'code',
}

export enum StrategyBuiltWithTypes {
  'equity_builder' = 'equity_builder',
  'option_builder' = 'option_builder',
  'code_builder' = 'code_builder',
}
